import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { MainTemplate } from 'templates';
import { LoadingIndicator } from 'atoms';
import { useStudentDetail, useStudentDeepLinkState } from 'core/hooks';
import type { StudentAlertBlock } from 'core/utils/studentAlerts';
import { PATHS } from 'router/config';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import { HeaderBar } from './HeaderBar';
import { KpiStrip } from './KpiStrip';
import { TAB_IDS } from './tabIds';
import { StudentTabBar } from './parts/StudentTabBar';
import { AlertsPanel } from './parts/AlertsPanel';
import {
  AgreementsTab,
  CommunicationTab,
  InvoicesTab,
  OverviewTab,
  PandaDocTab,
  PaymentsTab,
  TasksTab,
} from './tabs';

/**
 * `/students/:id/details` — the «student control center».
 *
 * Hybrid layout (see `~/.claude/plans/rustling-humming-peacock.md`):
 *   - Header with identity + quick action buttons.
 *   - KPI strip (per-currency totals across active agreements).
 *   - Overview tab is a dense dashboard (Contact / Groups / Agreement
 *     cards with inline payments+invoices+requisites).
 *   - Tabs Agreements / Payments / Invoices / PandaDoc are flat tables
 *     for filter-driven drill-down.
 *
 * All mutations route through existing popups (`StudentPaymentPopup`,
 * `EditAgreementPopup`, `LinkInvoicePaymentPopup`); after a successful
 * mutation we call `refetch()` so KPIs / progress bars stay accurate.
 */
export const StudentDetail = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const { state, setTab, applyAlert } = useStudentDeepLinkState();
  const { tab } = state;

  const { student, loading, error, refetch } = useStudentDetail(id);

  // In-tab scroll anchors brought into view after an alert/header navigation.
  const orphansRef = useRef<HTMLDivElement>(null);
  const payablesRef = useRef<HTMLDivElement>(null);
  const needsLinkingRef = useRef<HTMLDivElement>(null);
  const [pendingBlock, setPendingBlock] = useState<StudentAlertBlock | null>(
    null,
  );

  const refForBlock = useCallback((block: StudentAlertBlock | null) => {
    switch (block) {
      case 'orphanInvoices':
        return orphansRef;
      case 'accountsPayable':
        return payablesRef;
      case 'needsLinking':
        return needsLinkingRef;
      default:
        return null;
    }
  }, []);

  // After a deep-link tab switch, scroll the pending block into view once its
  // tab has mounted. `scrollIntoView` is guarded for jsdom safety.
  useEffect(() => {
    if (!pendingBlock) return undefined;
    const target = refForBlock(pendingBlock)?.current;
    const timer = window.setTimeout(() => {
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setPendingBlock(null);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [pendingBlock, tab, refForBlock]);

  // Single atomic alert navigation: set tab + filter via the deep-link hook,
  // then queue the in-tab scroll (Req 6.8). Replace-not-merge is in the hook.
  const onAlertNavigate = useCallback(
    (target: Parameters<typeof applyAlert>[0]) => {
      applyAlert(target);
      setPendingBlock(target.block ?? null);
    },
    [applyAlert],
  );

  const onScrollToOrphans = useCallback(() => {
    onAlertNavigate({ tab: 'overview', block: 'orphanInvoices' });
  }, [onAlertNavigate]);

  if (loading && !student) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.CenterLoader>
            <LoadingIndicator />
          </S.CenterLoader>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  if (!student) {
    const hasGqlErrors = Boolean(error?.graphQLErrors?.length);
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.BackLink onClick={() => history.push(PATHS.students)}>
            {t('pages.studentDetail.backToList')}
          </S.BackLink>
          <S.Banner variant="warn">
            <S.BannerTitle>
              {t('pages.studentDetail.errors.loadFailedTitle')}
            </S.BannerTitle>
            {hasGqlErrors ? (
              <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                {error!.graphQLErrors.map((e, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i}>
                    <code>
                      {e.path?.join('.') || '—'}: {e.message}
                    </code>
                  </li>
                ))}
              </ul>
            ) : (
              <div>{t('pages.studentDetail.errors.notFound', { id })}</div>
            )}
            {error?.networkError ? (
              <div style={{ marginTop: 6 }}>
                <strong>Network:</strong> {error.networkError.message}
              </div>
            ) : null}
          </S.Banner>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.BackLink onClick={() => history.push(PATHS.students)}>
          {t('pages.studentDetail.backToList')}
        </S.BackLink>

        {error ? (
          <S.Banner variant="warn">
            <S.BannerTitle>
              {t('pages.studentDetail.errors.partialTitle')}
            </S.BannerTitle>
            {error.graphQLErrors?.length
              ? error.graphQLErrors
                  .map((e) => `${e.path?.join('.') || '—'}: ${e.message}`)
                  .join(' · ')
              : error.message}
          </S.Banner>
        ) : null}

        <HeaderBar
          student={student}
          onScrollToOrphans={onScrollToOrphans}
          onNavigate={onAlertNavigate}
        />

        <KpiStrip student={student} />

        <AlertsPanel student={student} onNavigate={onAlertNavigate} />

        <StudentTabBar tabs={TAB_IDS} active={tab} onSelect={setTab} />

        {tab === 'overview' && (
          <OverviewTab
            ref={orphansRef}
            payablesRef={payablesRef}
            student={student}
            onChanged={refetch}
          />
        )}
        {tab === 'agreements' && (
          <AgreementsTab student={student} onChanged={refetch} />
        )}
        {tab === 'payments' && (
          <PaymentsTab
            ref={needsLinkingRef}
            student={student}
            onChanged={refetch}
          />
        )}
        {tab === 'invoices' && (
          <InvoicesTab student={student} onChanged={refetch} />
        )}
        {tab === 'pandadoc' && (
          <PandaDocTab student={student} onChanged={refetch} />
        )}
        {tab === 'communication' && <CommunicationTab student={student} />}
        {tab === 'tasks' && <TasksTab studentId={student.id} />}
      </S.Wrapper>
    </MainTemplate>
  );
});
